import { useEffect, useState } from 'react';
import { songsApi } from '@/api/songs';
import { setlistsApi } from '@/api/setlists';
import { apiErrorMessage } from '@/utils/apiError';
import type { SongListItem } from '@/types/song';
import { formatSongKey } from '@/utils/songKey';

const PAGE_SIZE = 50;

interface Props {
  teamId: string;
  ministryId: string;
  setlistId: string;
  existingSongIds: string[];
  onClose: () => void;
  onAdded: () => Promise<void>;
}

export default function AddSongToSetlistModal({
  teamId,
  ministryId,
  setlistId,
  existingSongIds,
  onClose,
  onAdded,
}: Props) {
  const [songs, setSongs] = useState<SongListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setSongs([]);
    songsApi.list(ministryId, { search, limit: PAGE_SIZE, offset: 0 }).then((response) => {
      if (active) {
        setSongs(response.data.results);
        setTotalCount(response.data.count);
      }
    }).catch((requestError: unknown) => {
      if (active) setError(apiErrorMessage(requestError, 'Erro ao carregar músicas.'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [ministryId, search]);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const response = await songsApi.list(ministryId, {
        search,
        limit: PAGE_SIZE,
        offset: songs.length,
      });
      setSongs((previous) => [...previous, ...response.data.results]);
      setTotalCount(response.data.count);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Erro ao carregar mais músicas.'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAdd = async (song: SongListItem) => {
    setAddingId(song.id);
    setError('');
    try {
      await setlistsApi.addItem(teamId, setlistId, song.id);
      setAddedIds((previous) => new Set([...previous, song.id]));
      await onAdded();
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Não foi possível adicionar a música.'));
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-song-to-setlist-title"
        className="bg-[#0D0C09] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <h3 id="add-song-to-setlist-title" className="text-white font-medium text-base">
              Adicionar música ao setlist
            </h3>
            <p className="text-white/40 text-xs mt-0.5">Escolha uma música da biblioteca da equipe.</p>
          </div>
          <button
            type="button"
            aria-label="Fechar seleção de músicas"
            onClick={onClose}
            className="text-white/40 hover:text-white/80 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </header>

        <div className="p-5">
          <label className="block mb-4">
            <span className="sr-only">Buscar músicas da biblioteca</span>
            <input
              type="search"
              value={search}
              disabled={loadingMore}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar músicas..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-[#C8A96E]/50"
            />
          </label>

          {error && <p role="alert" className="text-red-400 text-xs mb-3">{error}</p>}

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div role="status" className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
              </div>
            ) : songs.length === 0 ? (
              <p className="text-white/35 text-sm text-center py-8">
                {search ? 'Nenhuma música encontrada.' : 'A biblioteca não tem músicas ainda.'}
              </p>
            ) : (
              songs.map((song) => {
                const added = existingSongIds.includes(song.id) || addedIds.has(song.id);
                const adding = addingId === song.id;
                return (
                  <div key={song.id} className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0">
                    <i className="ri-music-2-line text-white/30 text-sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{song.title}</p>
                      <p className="text-white/40 text-xs truncate">
                        {[song.artist, formatSongKey(song.key, song.mode)].filter(Boolean).join(' · ') || 'Sem detalhes'}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label={added ? `${song.title} já está no setlist` : `Adicionar ${song.title} ao setlist`}
                      onClick={() => void handleAdd(song)}
                      disabled={added || addingId !== null}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        added
                          ? 'bg-green-500/15 text-green-400 cursor-default'
                          : 'bg-[#C8A96E]/20 text-[#C8A96E] hover:bg-[#C8A96E]/30 disabled:opacity-50'
                      }`}
                    >
                      {added ? 'No setlist' : adding ? 'Adicionando…' : 'Adicionar'}
                    </button>
                  </div>
                );
              })
            )}
            {!loading && songs.length < totalCount && (
              <button
                type="button"
                onClick={() => void handleLoadMore()}
                disabled={loadingMore}
                className="w-full mt-3 py-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:bg-white/5 text-xs disabled:opacity-50"
              >
                {loadingMore ? 'Carregando…' : 'Carregar mais músicas'}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
